import { resolveEasing } from '../easing.js';

// zones wider than this share of the scrollport would overlap each other
const MAX_ZONE_RATIO = 0.45;
// progress is written at 1/1000 resolution — finer changes are invisible
const PROGRESS_STEPS = 1000;

const EDGE_START = -1;
const EDGE_NONE = 0;
const EDGE_END = 1;

// The [pinning] paint-order window differs between the two pin mechanisms: a
// scroll-driven pin releases its content once the fill has carried it back off
// the edge (two item-lengths of travel), while the sticky fallback parks the
// box at the edge indefinitely.
const SUPPORTS_VIEW_TIMELINES =
	typeof CSS !== 'undefined' && CSS.supports('animation-timeline: view()');

const clamp01 = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);

/**
 * A natively scrolling list whose items animate at the scrollport edges instead
 * of clipping. Per coalesced frame it computes each item's edge-overshoot
 * progress from cached offsets, eases it, and writes `--scroll-stack-progress`
 * and `--scroll-stack-edge` on the item — every visual effect is plain CSS
 * keyed off those two values.
 *
 * The default `stack-fade` effect pins the exiting item at the start edge — a
 * CSS scroll-driven animation counter-translating the content along a
 * view-timeline the item declares, so the handoff costs no positional JS at all
 * and the item stays an ordinary flow box.
 * @class ScrollStack
 * @extends HTMLElement
 */
export class ScrollStack extends HTMLElement {
	static observedAttributes = [
		'direction',
		'effect',
		'zone',
		'edges',
		'easing',
		'reduced-motion',
		'disabled',
		'snap',
		'pin',
		'per-view',
		'item-min',
		'item-max',
	];

	#items = [];
	#contents = [];
	#starts = [];
	#lengths = [];
	#lastProgress = [];
	#lastEdge = [];
	#lastPinning = [];
	#hidden = [];
	#portLength = 0;
	#scrollMax = 0;
	#zoneLength = 0;
	#zonePerItem = true;
	#itemLength = null;
	#easing = resolveEasing('linear');
	#rafId = null;
	#needsMeasure = true;
	#initialized = false;
	#listening = false;
	#resizeObserver = null;
	#mutationObserver = null;
	#warnedOffsetParent = false;
	#warnedMissingContent = false;
	#warnedPerView = false;
	#warnedPin = false;

	constructor() {
		super();
		const _ = this;
		_.handlers = {
			scroll: () => _.#schedule(),
			resize: () => _.refresh(),
			mutate: () => _.#handleMutation(),
		};
	}

	connectedCallback() {
		// when the element is defined before the parser reaches its children
		// (e.g. a synchronous script in <head>), connectedCallback fires with
		// an empty subtree — defer initialization until parsing finishes
		if (!this.querySelector(':scope > scroll-stack-item') && document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
			return;
		}
		this.#init();
	}

	disconnectedCallback() {
		this.detachListeners();
		this.#cancelFrame();
	}

	attributeChangedCallback(name, previousValue, currentValue) {
		if (previousValue === currentValue || !this.#initialized) return;

		if (name === 'disabled') {
			if (this.disabled) {
				this.#deactivate();
			} else {
				this.#activate();
			}
			return;
		}

		if (name === 'direction') {
			this.style.removeProperty('scroll-padding-block');
			this.style.removeProperty('scroll-padding-inline');
		}

		// effect and reduced-motion are handled entirely in CSS, but a swapped
		// effect changes the transform-origin rule, so every attribute here
		// forces one full pass with the caches cleared
		this.refresh();
	}

	/** @returns {'vertical' | 'horizontal'} the scroll axis */
	get direction() {
		return this.getAttribute('direction') === 'horizontal' ? 'horizontal' : 'vertical';
	}

	/** @returns {string} the active effect name */
	get effect() {
		// `??`, not `||`: effect="" has to read as an unrecognized effect, exactly
		// as the CSS `:is(:not([effect]), [effect='stack-fade'])` group sees it
		return this.getAttribute('effect') ?? 'stack-fade';
	}

	/** @returns {'both' | 'start' | 'end'} which scrollport edges animate */
	get edges() {
		const value = this.getAttribute('edges');
		return value === 'start' || value === 'end' ? value : 'both';
	}

	/**
	 * Which edges hold an item still while its flow position keeps moving.
	 * An explicit attribute always wins. With none, `stack-fade` (the default
	 * effect) implies `start` and every other effect implies `none` — and the
	 * implied `start` is dropped under `edges="end"`, which removes the start
	 * edge entirely. Mirrors the CSS selectors exactly.
	 * @returns {'start' | 'end' | 'both' | 'none'} the resolved pin
	 */
	get pin() {
		const raw = this.getAttribute('pin');
		if (raw === 'start' || raw === 'end' || raw === 'both' || raw === 'none') return raw;
		if (raw !== null) this.#warnPin(raw);
		if (this.effect !== 'stack-fade' || this.edges === 'end') return 'none';
		return 'start';
	}

	/**
	 * Scroll snapping. Presence alone means mandatory snapping to item starts;
	 * `center` snaps centres, also mandatory; `proximity` is the softer opt-out
	 * and keeps start alignment. Mandatory is only safe because items are plain
	 * flow boxes — see the snap note in the stylesheet.
	 * @returns {'start' | 'center' | 'proximity' | null} null when off
	 */
	get snap() {
		if (!this.hasAttribute('snap')) return null;
		const value = this.getAttribute('snap');
		return value === 'center' || value === 'proximity' ? value : 'start';
	}

	/**
	 * The resolved per-item length in pixels, or null when the list is not
	 * sized by `per-view`/`item-min`/`item-max`.
	 * @returns {number | null}
	 */
	get itemLength() {
		return this.#itemLength;
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(value) {
		this.toggleAttribute('disabled', Boolean(value));
	}

	/**
	 * Current eased progress per item, 0 (fully in view) → 1 (fully faded).
	 * @returns {Map<HTMLElement, number>} a fresh map, safe to mutate
	 */
	get progress() {
		const map = new Map();
		this.#items.forEach((item, index) => {
			const value = this.#lastProgress[index];
			// the cache is seeded with -1 to force the first write; that sentinel
			// is an implementation detail and must never leave the component
			map.set(item, value > 0 ? value : 0);
		});
		return map;
	}

	/**
	 * Re-measures every item and forces one full pass. Call after changing item
	 * content in a way the observers can't see (a font swap, an image decode).
	 */
	refresh() {
		this.#resolveEasing();
		this.#resetCaches();
		this.#needsMeasure = true;
		this.#schedule();
	}

	queryDOM() {
		const _ = this;
		const previousItems = _.#items;
		const previousContents = _.#contents;
		const candidates = [..._.querySelectorAll(':scope > scroll-stack-item')];
		const items = [];
		const contents = [];
		for (const item of candidates) {
			const content = item.firstElementChild;
			if (!content) {
				_.#warnMissingContent();
				continue;
			}
			items.push(item);
			contents.push(content);
		}

		// an item that drops out of the tracked set — pulled from the DOM, or it
		// lost the element child the effects apply to — keeps its last vars, its
		// will-change and its pointer-events: none unless they are taken off here
		const kept = new Set(items);
		previousItems.forEach((item, index) => {
			if (kept.has(item)) return;
			_.#clearItem(item, previousContents[index]);
		});

		_.#items = items;
		_.#contents = contents;
		// hidden-state is event bookkeeping, not a write cache: it survives every
		// refresh and is only rebuilt when the item set itself changes
		_.#hidden = new Array(items.length).fill(null);
		_.#resetCaches();
	}

	attachListeners() {
		const _ = this;
		if (_.#listening) return;
		_.#listening = true;
		_.addEventListener('scroll', _.handlers.scroll, { passive: true });
		_.#resizeObserver = new ResizeObserver(_.handlers.resize);
		_.#resizeObserver.observe(_);
		for (const item of _.#items) _.#resizeObserver.observe(item);
		_.#mutationObserver = new MutationObserver(_.handlers.mutate);
		_.#mutationObserver.observe(_, { childList: true });
	}

	detachListeners() {
		const _ = this;
		if (!_.#listening) return;
		_.#listening = false;
		_.removeEventListener('scroll', _.handlers.scroll);
		_.#resizeObserver?.disconnect();
		_.#resizeObserver = null;
		_.#mutationObserver?.disconnect();
		_.#mutationObserver = null;
	}

	#init() {
		const _ = this;
		_.#initialized = true;
		_.#resolveEasing();
		_.queryDOM();
		if (_.disabled) return;
		_.#activate();
	}

	#activate() {
		// re-scan before the observers attach: the MutationObserver was
		// disconnected while disabled, so items added in the meantime predate
		// observation and would otherwise stay untracked until the next childList
		// mutation — and attachListeners observes the item set it finds
		this.queryDOM();
		this.attachListeners();
		this.refresh();
	}

	#deactivate() {
		const _ = this;
		_.detachListeners();
		_.#cancelFrame();
		_.style.removeProperty('scroll-padding-block');
		_.style.removeProperty('scroll-padding-inline');
		_.#items.forEach((item, index) => _.#clearItem(item, _.#contents[index]));
		_.#resetCaches();
	}

	#handleMutation() {
		const _ = this;
		_.queryDOM();
		if (_.#resizeObserver) {
			_.#resizeObserver.disconnect();
			_.#resizeObserver.observe(_);
			for (const item of _.#items) _.#resizeObserver.observe(item);
		}
		_.#needsMeasure = true;
		_.#schedule();
	}

	/**
	 * Drops the per-frame write caches so the next pass rewrites every item. Both
	 * sentinels have to be values a real pass can never produce: `#lastEdge` is
	 * seeded with null rather than EDGE_NONE because an item resting at edge 0
	 * would otherwise match the sentinel, skip the edge write, and keep whatever
	 * stale `--scroll-stack-edge` it carried into the reset — which is exactly
	 * what the end-edge z-index inversion reads.
	 */
	#resetCaches() {
		this.#lastProgress = new Array(this.#items.length).fill(-1);
		this.#lastEdge = new Array(this.#items.length).fill(null);
		this.#lastPinning = new Array(this.#items.length).fill(null);
	}

	/**
	 * Removes every var, state attribute and transform-origin the component ever
	 * wrote to one item.
	 * @param {HTMLElement} item - the item wrapper
	 * @param {HTMLElement} [content] - its content element, if it still has one
	 */
	#clearItem(item, content) {
		item.style.removeProperty('--scroll-stack-progress');
		item.style.removeProperty('--scroll-stack-edge');
		item.removeAttribute('transitioning');
		item.removeAttribute('fully-hidden');
		item.removeAttribute('pinning');
		content?.style.removeProperty('transform-origin');
	}

	#resolveEasing() {
		this.#easing = resolveEasing(this.getAttribute('easing'));
	}

	#schedule() {
		const _ = this;
		if (_.#rafId !== null) return;
		_.#rafId = requestAnimationFrame(() => {
			_.#rafId = null;
			_.#update();
		});
	}

	#cancelFrame() {
		if (this.#rafId === null) return;
		cancelAnimationFrame(this.#rafId);
		this.#rafId = null;
	}

	/**
	 * Caches every item's offset and length along the active axis. Offsets ignore
	 * transforms and cost no layout in the per-frame path — this is the only
	 * place the component reads layout.
	 */
	#measure() {
		const _ = this;
		const horizontal = _.direction === 'horizontal';

		// sizing changes layout, so it has to settle before anything positional is
		// read — the first offset read below forces the reflow that applies it
		_.#resolveSizing();

		_.#portLength = horizontal ? _.clientWidth : _.clientHeight;
		_.#scrollMax = Math.max(
			0,
			horizontal ? _.scrollWidth - _.clientWidth : _.scrollHeight - _.clientHeight
		);

		// Fallback path only. Nothing is sticky where scroll-driven animations are
		// supported, so offsets are always honest there — but a browser on the
		// @supports-not path still gets the sticky pin, and a stuck item reports
		// its displaced position through offsetTop rather than its flow position.
		// The attribute drives a `position: static !important` rule for the
		// duration of the reads; it runs pre-paint inside the rAF, so the swap
		// never renders, and on the animation path it matches nothing.
		_.setAttribute('measuring', '');

		// offsetTop/offsetLeft measure from the scroller's border edge, scrollTop
		// from its padding edge — a bordered scroller needs the difference removed
		const borderOffset = horizontal ? _.clientLeft : _.clientTop;
		const starts = new Array(_.#items.length);
		const lengths = new Array(_.#items.length);
		let rootRect = null;
		_.#items.forEach((item, index) => {
			if (item.offsetParent === _) {
				starts[index] = (horizontal ? item.offsetLeft : item.offsetTop) - borderOffset;
				lengths[index] = horizontal ? item.offsetWidth : item.offsetHeight;
				return;
			}
			// an item whose offsetParent isn't the scroller (a positioned ancestor
			// slipped in, or the item is display:none) can't be measured by offset —
			// fall back to rect deltas for this pass
			_.#warnOffsetParent();
			rootRect = rootRect || _.getBoundingClientRect();
			const rect = item.getBoundingClientRect();
			starts[index] = horizontal
				? rect.left - rootRect.left - borderOffset + _.scrollLeft
				: rect.top - rootRect.top - borderOffset + _.scrollTop;
			lengths[index] = horizontal ? rect.width : rect.height;
		});
		_.#starts = starts;
		_.#lengths = lengths;

		_.removeAttribute('measuring');

		_.#resolveZone();
	}

	/**
	 * Resolves `per-view` / `item-min` / `item-max` into a single root-level
	 * `--scroll-stack-item-length`, which CSS feeds to every item's flex-basis.
	 * With none of the three set, no var is written and items keep their natural
	 * size. The write is skipped when the value is unchanged — it reflows.
	 */
	#resolveSizing() {
		const _ = this;
		const perViewRaw = (_.getAttribute('per-view') || '').trim();
		const minimumRaw = (_.getAttribute('item-min') || '').trim();
		const maximumRaw = (_.getAttribute('item-max') || '').trim();

		if (!perViewRaw && !minimumRaw && !maximumRaw) {
			if (_.#itemLength !== null) {
				_.#itemLength = null;
				_.style.removeProperty('--scroll-stack-item-length');
			}
			return;
		}

		const horizontal = _.direction === 'horizontal';
		const style = getComputedStyle(_);
		// clientWidth/clientHeight include the scroller's own padding, which no
		// item can occupy
		const port = horizontal ? _.clientWidth : _.clientHeight;
		const padding = horizontal
			? (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
			: (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
		const available = Math.max(0, port - padding);
		const gap = parseFloat(horizontal ? style.columnGap : style.rowGap) || 0;

		const minimum = _.#resolveLength(minimumRaw, port);
		const maximum = _.#resolveLength(maximumRaw, port);
		const count = _.#resolvePerView(perViewRaw);

		let slot;
		if (count !== null) {
			slot = (available - (count - 1) * gap) / count;
			if (minimum !== null) slot = Math.max(slot, minimum);
			if (maximum !== null) slot = Math.min(slot, maximum);
		} else {
			// auto: the most whole items that each still clear item-min. Solving
			// (available - (n-1)*gap) / n >= minimum for n gives the floor below.
			const fit = minimum > 0 ? Math.max(1, Math.floor((available + gap) / (minimum + gap))) : 1;
			slot = (available - (fit - 1) * gap) / fit;
			// an item-max smaller than the fitted slot wins, and the row simply
			// stops filling the port exactly — documented behavior, not a bug
			if (maximum !== null && slot > maximum) slot = maximum;
		}

		const resolved = Math.max(0, Math.round(slot * 100) / 100);
		if (resolved === _.#itemLength) return;
		_.#itemLength = resolved;
		_.style.setProperty('--scroll-stack-item-length', `${resolved}px`);
	}

	/**
	 * @param {string} raw - the `per-view` attribute value
	 * @returns {number | null} a positive integer, or null for `auto` and for
	 *   an absent value (both of which mean "fit as many as item-min allows")
	 */
	#resolvePerView(raw) {
		if (!raw || raw === 'auto') return null;
		const count = Number(raw);
		if (Number.isInteger(count) && count >= 1) return count;
		if (!this.#warnedPerView) {
			this.#warnedPerView = true;
			console.warn(
				`<scroll-stack> per-view="${raw}" is not a positive integer or "auto" — ` +
					'falling back to auto.'
			);
		}
		return null;
	}

	/**
	 * Resolves an `item-min`/`item-max` value to pixels. `%` is a share of the
	 * scrollport, `rem` multiplies the root font size, and a bare number is px.
	 * @param {string} raw - the authored length
	 * @param {number} port - the scrollport length along the active axis
	 * @returns {number | null} pixels, or null when unset or unparseable
	 */
	#resolveLength(raw, port) {
		if (!raw) return null;
		const value = parseFloat(raw);
		if (!Number.isFinite(value) || value < 0) return null;
		if (raw.endsWith('%')) return (value / 100) * port;
		if (raw.endsWith('rem')) {
			const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
			return value * rootSize;
		}
		return value;
	}

	/**
	 * Resolves the `zone` attribute into a pixel length. `item` defers to each
	 * item's own length at update time; every zone is capped at 45% of the
	 * scrollport so the two edge zones can never overlap.
	 */
	#resolveZone() {
		const _ = this;
		const cap = _.#portLength * MAX_ZONE_RATIO;
		const raw = (_.getAttribute('zone') || 'item').trim();
		_.#zonePerItem = raw === 'item' || raw === '';
		_.#zoneLength = 0;

		if (!_.#zonePerItem) {
			const value = parseFloat(raw);
			if (Number.isFinite(value) && value > 0) {
				_.#zoneLength = raw.endsWith('%') ? (value / 100) * _.#portLength : value;
			} else {
				_.#zonePerItem = true;
			}
		}

		if (!_.#zonePerItem) _.#zoneLength = Math.min(_.#zoneLength, cap);

		const property =
			_.direction === 'horizontal' ? 'scroll-padding-inline' : 'scroll-padding-block';

		// scroll-padding shifts every snap position by the same amount, so a
		// snapping list can't carry it without the snapped items landing short of
		// the edge they were meant to align to. It isn't needed there either: a
		// snapped resting position is fully visible by construction, which is the
		// exact guarantee the padding existed to provide for focus scrolling.
		if (_.snap) {
			_.style.removeProperty(property);
			return;
		}

		// focus-driven scrolling must land elements past the fade zone, never
		// focused-but-invisible
		let padding = 0;
		if (_.#zonePerItem) {
			for (const length of _.#lengths) padding = Math.max(padding, Math.min(length, cap));
		} else {
			padding = _.#zoneLength;
		}
		_.style.setProperty(property, `${Math.round(padding)}px`);
	}

	#zoneFor(index) {
		if (!this.#zonePerItem) return this.#zoneLength;
		return Math.min(this.#lengths[index], this.#portLength * MAX_ZONE_RATIO);
	}

	/**
	 * The whole per-frame job: pure arithmetic over cached offsets, then at most
	 * a couple of style writes for the items actually inside an edge zone.
	 */
	#update() {
		const _ = this;
		if (_.disabled) return;
		if (_.#needsMeasure) {
			_.#measure();
			_.#needsMeasure = false;
		}

		const horizontal = _.direction === 'horizontal';
		const scrollPosition = horizontal ? _.scrollLeft : _.scrollTop;
		const portLength = _.#portLength;
		const scrollMax = _.#scrollMax;
		const edges = _.edges;
		const skipStart = edges === 'end';
		const skipEnd = edges === 'start';
		const pinsStart = _.#pinsAt(EDGE_START);
		const pinsEnd = _.#pinsAt(EDGE_END);
		const events = [];

		for (let index = 0; index < _.#items.length; index += 1) {
			const zone = _.#zoneFor(index);
			const length = _.#lengths[index];
			const itemStart = _.#starts[index] - scrollPosition;
			const itemEnd = itemStart + length;
			let progress = 0;
			let edge = EDGE_NONE;

			// [pinning] tracks the PIN's overlap window, not the effect zone: the
			// counter-translate holds content over a neighbor for the item's whole
			// edge crossing, however narrow the zone is — and under edges="start"
			// pin="end" it runs with no effect progress at all. On the animation
			// path the fill releases the content after two item-lengths of travel;
			// the sticky fallback parks the box at the edge for good.
			const pinning =
				(pinsStart &&
					itemStart < 0 &&
					(!SUPPORTS_VIEW_TIMELINES || itemStart > -2 * length)) ||
				(pinsEnd &&
					itemEnd > portLength &&
					(!SUPPORTS_VIEW_TIMELINES || itemEnd < portLength + 2 * length));
			if (pinning !== _.#lastPinning[index]) {
				_.#items[index].toggleAttribute('pinning', pinning);
				_.#lastPinning[index] = pinning;
			}

			if (zone > 0) {
				// boundary damping: nothing animates at an edge it can't scroll past
				const startProgress = skipStart
					? 0
					: Math.min(clamp01((zone - itemEnd) / zone), clamp01(scrollPosition / zone));
				const endProgress = skipEnd
					? 0
					: Math.min(
							clamp01((zone - (portLength - itemStart)) / zone),
							clamp01((scrollMax - scrollPosition) / zone)
						);

				if (startProgress >= endProgress) {
					progress = startProgress;
					edge = EDGE_START;
				} else {
					progress = endProgress;
					edge = EDGE_END;
				}
				if (progress <= 0) {
					progress = 0;
					edge = EDGE_NONE;
				}
			}

			const eased = progress > 0 ? clamp01(_.#easing(progress)) : 0;
			const quantized = Math.round(eased * PROGRESS_STEPS) / PROGRESS_STEPS;
			if (quantized === _.#lastProgress[index] && edge === _.#lastEdge[index]) continue;

			_.#writeItem({ index, progress: quantized, edge, events });
		}

		// dispatched after the loop, never from inside it: a listener that calls
		// refresh() or flips `disabled` swaps the write caches out mid-pass, and
		// every item still to come would then repopulate the fresh caches with
		// values the forced full pass skips as "unchanged"
		for (const event of events) _.dispatchEvent(event);
	}

	/**
	 * Writes one item's vars, transform-origin and state attributes, and queues
	 * any hidden/shown event onto `events` for the caller to dispatch once the
	 * whole pass is over. Only reached when something actually changed.
	 * @param {{ index: number, progress: number, edge: number,
	 *   events: CustomEvent[] }} state
	 */
	#writeItem({ index, progress, edge, events }) {
		const _ = this;
		const item = _.#items[index];
		const content = _.#contents[index];

		item.style.setProperty('--scroll-stack-progress', progress);
		// the edge var is written back to 0 on leaving a zone as well. Nothing
		// internal reads it any more — paint order moved to the state attributes —
		// but it is public API for author effects, and a stale 1 on an item that
		// is back in view would make every directional effect point the wrong way
		if (edge !== _.#lastEdge[index]) {
			item.style.setProperty('--scroll-stack-edge', edge);
			if (edge !== EDGE_NONE) content.style.transformOrigin = _.#originFor(edge);
		}

		const transitioning = progress > 0 && progress < 1;
		const fullyHidden = progress >= 1;
		item.toggleAttribute('transitioning', transitioning);
		item.toggleAttribute('fully-hidden', fullyHidden);

		_.#lastProgress[index] = progress;
		_.#lastEdge[index] = edge;

		// tracked apart from #lastProgress on purpose: that cache is wiped by every
		// refresh, and comparing against a wiped value made an item that went
		// hidden, survived a resize, then scrolled back into view fire no
		// item-shown at all — leaving every listener stuck on "hidden"
		const wasFullyHidden = _.#hidden[index];
		_.#hidden[index] = fullyHidden;
		if (wasFullyHidden !== null && fullyHidden !== wasFullyHidden) {
			events.push(
				new CustomEvent(fullyHidden ? 'scroll-stack:item-hidden' : 'scroll-stack:item-shown', {
					bubbles: true,
					detail: { item, index, edge },
				})
			);
		}
	}

	/**
	 * Whether the resolved `pin` holds items still at the given edge.
	 * @param {number} edge - EDGE_START or EDGE_END
	 * @returns {boolean}
	 */
	#pinsAt(edge) {
		const pin = this.pin;
		if (pin === 'both') return true;
		if (pin === 'start') return edge === EDGE_START;
		if (pin === 'end') return edge === EDGE_END;
		return false;
	}

	/**
	 * A pinned edge recedes in place, so its content keeps a centered origin.
	 * An unpinned one is still travelling toward the clip boundary, so it takes
	 * its origin from the opposite edge of its own box and scales away from the
	 * boundary instead of being sliced by it.
	 * @param {number} edge - EDGE_START or EDGE_END
	 * @returns {string} a transform-origin value
	 */
	#originFor(edge) {
		const _ = this;
		if (_.#pinsAt(edge)) return '50% 50%';
		const horizontal = _.direction === 'horizontal';
		if (edge === EDGE_START) return horizontal ? '100% 50%' : '50% 100%';
		return horizontal ? '0% 50%' : '50% 0%';
	}

	#warnMissingContent() {
		if (this.#warnedMissingContent) return;
		this.#warnedMissingContent = true;
		console.warn(
			'<scroll-stack> skipped a <scroll-stack-item> with no element child — ' +
				"effects are applied to the item's content element, so each item needs one."
		);
	}

	#warnPin(raw) {
		if (this.#warnedPin) return;
		this.#warnedPin = true;
		console.warn(
			`<scroll-stack> pin="${raw}" is not one of start, end, both, none — ` +
				'falling back to the effect-implied default.'
		);
	}

	#warnOffsetParent() {
		if (this.#warnedOffsetParent) return;
		this.#warnedOffsetParent = true;
		console.warn(
			"<scroll-stack> an item's offsetParent is not the scroller — measuring it " +
				'by bounding rect instead. Remove any positioned wrapper between ' +
				'<scroll-stack> and its items.'
		);
	}
}

if (!customElements.get('scroll-stack')) {
	customElements.define('scroll-stack', ScrollStack);
}
